// Which of the offered stream servers to connect to.
//
// The server hands back a list of candidates, best-guess first, with the deployment
// that served this page LAST — it is the one that always works, so it is the floor,
// not the preference. Which of the others is actually best is a question only the
// browser can answer, because it depends on where the viewer is sitting.
//
// Only the viewer's own leg is measured, deliberately. For a viewer in the US and an
// app in Australia the totals barely differ — both cross the Pacific once — so what
// separates them is which network carries the long leg. Relay-to-app runs between AWS
// regions; viewer-to-relay runs over a consumer last mile, where a high RTT also means
// slower recovery from loss. Choosing the relay nearest the viewer puts the long haul
// on the better network.

import {AuthToken, EnsureFreshToken} from "./auth.js";

// Measurements are kept for the life of the page, keyed by the RELAY rather than by
// the stream URL: candidates carry ?app=, so keying on the whole URL would re-probe
// every relay each time the viewer opened a different app, to learn the same thing.
// A viewer's position relative to a fixed set of regions does not change mid-session.
const measured = new Map();

// A probe that has not answered by now is not a candidate worth waiting for. The
// origin is always in the list and always reachable, so a slow relay costs nothing to
// abandon.
const PROBE_TIMEOUT_MS = 2000;

// Samples per probe, of which the best is kept. Latency noise is one-sided — a request
// can be delayed by scheduling, a retransmit or a busy main thread, but nothing makes it
// arrive faster than the network allows — so the minimum of a few samples is a better
// estimate of the path than the mean of many, and this is what ping has always done.
const PROBE_SAMPLES = 3;

// How often the whole set is re-measured while a page is open.
//
// Measuring once was wrong for a tab that lives for hours: a laptop moves from wifi to
// tethering, a relay degrades, one is added or removed, and the ranking still reflects
// whatever was true at load. Re-measuring costs three ~40-byte responses per server per
// minute, against a video stream — it does not register.
const REMEASURE_INTERVAL_MS = 60_000;

// An explicit choice, stored beside the renderer and the video scale because it is a
// device and network preference rather than anything about the account — a laptop that
// roams needs a different answer from the desktop next to it, and neither is a property
// of who is signed in.
const KeyPreferredRelay = "preferred-relay";

// What this session is actually streaming through, which is not always what was
// preferred or what measured fastest: a candidate can be demoted by a failed probe.
// The menu reports this rather than the intention.
let connectedTo = null;

export function setConnectedServer(server) {
	connectedTo = server ? healthOrigin(server) : null;
}

export function connectedRelay() {
	return connectedTo;
}

/** The relay the viewer pinned, or null for automatic. */
export function preferredRelay() {
	try {
		return window.localStorage.getItem(KeyPreferredRelay);
	} catch {
		return null;
	}
}

export function setPreferredRelay(relay) {
	try {
		if (relay) window.localStorage.setItem(KeyPreferredRelay, relay);
		else window.localStorage.removeItem(KeyPreferredRelay);
	} catch {
		// Storage unavailable; the choice simply does not persist.
	}
}

/** The https origin serving a wss:// stream URL, which is where /health lives. */
export function healthOrigin(streamUrl) {
	const url = new URL(streamUrl);
	url.protocol = url.protocol === "ws:" ? "http:" : "https:";
	url.pathname = "/health";
	url.search = "";
	return url.toString();
}

/**
 * The best of a set of samples: the smallest that got an answer at all.
 *
 * Infinity when none did, which ranks the candidate behind every reachable one without
 * dropping it.
 * @param {number[]} samples
 */
export function bestSample(samples) {
	return Math.min(...samples, Infinity);
}

// Each sample gets its own URL so its Resource Timing entry can be found unambiguously
// and no cache along the way can answer it.
let probeCount = 0;

/** One sample: the round trip to a candidate's /health, or Infinity if it did not answer. */
async function sample(healthUrl) {
	const url = `${healthUrl}?p=${++probeCount}`;
	const started = performance.now();
	const abort = new AbortController();
	const timer = setTimeout(() => abort.abort(), PROBE_TIMEOUT_MS);
	try {
		// /health is unauthenticated and answers with CORS headers, so this needs no
		// token and no preflight. cache: "no-store" because a cached answer would
		// measure the disk.
		const response = await fetch(url, {cache: "no-store", signal: abort.signal});
		if (!response.ok) return Infinity;

		// Prefer what the browser measured over what this function can see. Wall clock
		// around fetch() also counts time queued behind the connection pool and behind a
		// busy main thread — a page decoding 60fps of video has plenty of both — while
		// responseStart - requestStart is the request leaving to the first byte arriving,
		// which is the round trip and nothing else. It needs Timing-Allow-Origin from the
		// server, which webremote sends; without it these read 0 and this falls through.
		const entry = /** @type {PerformanceResourceTiming | undefined} */ (
			performance.getEntriesByName(url).at(-1)
		);
		// Only ever the entry just made is wanted, and the resource buffer holds 250 by
		// default — a page probing two servers every minute fills it in well under an
		// hour, after which new entries are dropped and this silently falls back to the
		// biased wall-clock number for the rest of the session. Nothing else here reads
		// resource timings; the NATS client's perf wrapper records `measure` entries,
		// which this does not touch.
		performance.clearResourceTimings();
		if (entry && entry.requestStart > 0 && entry.responseStart > 0) {
			return entry.responseStart - entry.requestStart;
		}
		return performance.now() - started;
	} catch {
		// Unreachable, blocked, or too slow to bother with.
		return Infinity;
	} finally {
		clearTimeout(timer);
	}
}

/** The round trip to a candidate, best of PROBE_SAMPLES. */
async function probe(streamUrl) {
	const healthUrl = healthOrigin(streamUrl);
	const samples = [];
	// Sequentially: samples in parallel share a connection and a CPU, so they measure
	// each other's queueing rather than the path.
	for (let i = 0; i < PROBE_SAMPLES; i++) {
		const taken = await sample(healthUrl);
		// Nothing answered. Two more attempts will not change that, and every candidate
		// is waiting on this one.
		if (taken === Infinity) return bestSample(samples);
		samples.push(taken);
	}
	return bestSample(samples);
}

/**
 * Measure every candidate that has not been measured yet, in parallel.
 *
 * In parallel and once per page, not serially per stream: the previous version of this
 * opened a full WebSocket to each candidate in turn and waited for it to open, on every
 * single stream start, which cost more than the routing saved.
 * @param {string[]} servers
 */
export async function measureServers(servers, {force = false} = {}) {
	const wanted = force ? servers : servers.filter((s) => !measured.has(healthOrigin(s)));
	await Promise.all(
		wanted.map(async (server) => {
			measured.set(healthOrigin(server), await probe(server));
		}),
	);
	return new Map(measured);
}

/**
 * Ask what is on offer and measure it, returning the timings.
 *
 * Meant to be called as a page loads, so a ranking exists before a viewer picks an app
 * instead of being probed for at connect time. /api/servers is the candidate list without
 * an app, which is all a measurement needs: probes strip the query, so these are the same
 * timings the connect path then reuses.
 */
export async function measureOfferedServers({force = false} = {}) {
	await EnsureFreshToken();
	try {
		const response = await fetch("/api/servers", {
			method: "GET",
			headers: {Authorization: "Bearer " + AuthToken()},
		});
		if (!response.ok) return measurements();
		const {servers} = await response.json();
		if (!servers?.length) return measurements();
		return await measureServers(servers, {force});
	} catch (err) {
		// Nothing to recover: with no measurements a session connects to the deployment
		// that served the page, exactly as it did before relays existed.
		console.warn("could not measure stream servers", err);
		return measurements();
	}
}

/**
 * Keep the measurements current for as long as the page is open, calling back after each
 * round so the UI can follow.
 *
 * The list is re-fetched each round, not just re-measured, so a relay that starts or stops
 * announcing appears or disappears on its own — the same self-healing the server side has,
 * rather than a set fixed at load.
 *
 * Nothing here moves a running stream. A fresher ranking changes which server the NEXT
 * connect picks, and what the menu reports; switching mid-session would cost a reconnect
 * and a key frame for a decision the viewer did not ask for.
 * @param {() => void} [onRound]
 */
export function startMeasuring(onRound = () => {}) {
	let running = false;

	const round = async ({force}) => {
		// A hidden tab measures its own throttling, and answers nobody is looking at.
		if (document.hidden || running) return;
		running = true;
		try {
			await measureOfferedServers({force});
			onRound();
		} finally {
			running = false;
		}
	};

	round({force: false});
	setInterval(() => round({force: true}), REMEASURE_INTERVAL_MS);

	// Coming back to the tab or back onto a network are the two moments the stored numbers
	// are most likely to be stale — a laptop that slept, or moved to tethering.
	document.addEventListener("visibilitychange", () => {
		if (!document.hidden) round({force: true});
	});
	window.addEventListener("online", () => round({force: true}));
}

/**
 * The candidates in the order they should be tried: fastest first, with anything
 * unreachable after them.
 *
 * The deployment that served the page competes on its measurement like any other
 * candidate — for a viewer sitting next to it, it IS the right answer, and a viewer in
 * London must not be sent to Virginia to reach an app in London. What makes it the floor
 * is that it is always in the list and always reachable, not that it is last.
 *
 * If nothing could be measured — no relay reachable, or the probes blocked entirely —
 * this returns the server's own order, which ends with that deployment, so the session
 * connects exactly where it does today.
 * @param {string[]} servers
 * @param {Map<string, number>} timings
 */
export function rankServers(servers, timings, preferred = preferredRelay()) {
	// An explicit choice wins over measurement, but only while it is still on offer:
	// a pinned relay that has stopped announcing must not strand the viewer.
	const pinned = servers.find((server) => healthOrigin(server) === preferred);
	if (pinned) {
		return [pinned, ...servers.filter((server) => server !== pinned)];
	}
	const ranked = [...servers].sort((a, b) => {
		const timeA = timings.get(healthOrigin(a)) ?? Infinity;
		const timeB = timings.get(healthOrigin(b)) ?? Infinity;
		if (timeA !== timeB) return timeA - timeB;
		// Unmeasurable, so keep the server's own order rather than inventing one.
		return servers.indexOf(a) - servers.indexOf(b);
	});

	if ((timings.get(healthOrigin(ranked[0])) ?? Infinity) === Infinity) {
		return servers;
	}
	return ranked;
}

/** The measured round trip to a candidate, or null when it could not be reached. */
export function measurementFor(server) {
	const time = measured.get(healthOrigin(server));
	return time === undefined || time === Infinity ? null : Math.round(time);
}

/** Everything measured so far, keyed by relay, for the UI to list. */
export function measurements() {
	return new Map(measured);
}
